import React, { useEffect, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { getOrderById } from '@/modules/orders/redux/OrderCRUD';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { AddButtonAutocomplete } from '@/__core/hocs';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

import { RequestMaterialEmployeeOption, RequestMaterialFormData, RequestMaterialItemOption, RequestMaterialLine, RequestMaterialOrderOption, RequestMaterialUomOption } from '../types';
import RequestMaterialItemsTable from './RequestMaterialItemsTable';

interface RequestMaterialFormProps {
  formData: RequestMaterialFormData;
  warehouseOptions: Array<{ id: string; name: string }>;
  orderOptions: RequestMaterialOrderOption[];
  orderLoading?: boolean;
  statusOptions: Array<{ id: string; name: string; code?: string; color?: string }>;
  itemOptions: RequestMaterialItemOption[];
  uomOptions: RequestMaterialUomOption[];
  employeeOptions: RequestMaterialEmployeeOption[];
  companyName?: string;
  materialRequestId?: string;
  onChangeForm: (updates: Partial<RequestMaterialFormData>) => void;
  onChangeLine: (lineId: number, updates: Partial<RequestMaterialLine>) => void;
  onAddItem: () => void;
  onRemoveLine: (lineId: number) => void;
  onReorderLines: (lines: RequestMaterialLine[]) => void;
  onCreateStatus: (payload: { name: string }) => Promise<{ id: string; name: string }>;
  onCreateItem: (item: RequestMaterialItemOption) => void;
  onCreateUom: (payload: { name: string; conversion?: { to_uom_id: string; ratio: number } }) => Promise<RequestMaterialUomOption>;
  onSave: () => void;
  onCancel?: () => void;
  saving?: boolean;
  canEdit?: boolean;
  /** Chỉ bật Lưu sau khi người dùng đã chỉnh sửa form */
  isDirty?: boolean;
}

const normalizeStatusValue = (value?: string | null) => (value || '').trim().toUpperCase();

export default function RequestMaterialForm({
  formData,
  warehouseOptions,
  orderOptions,
  orderLoading = false,
  statusOptions,
  itemOptions,
  uomOptions,
  employeeOptions,
  companyName,
  materialRequestId,
  onChangeForm,
  onChangeLine,
  onAddItem,
  onRemoveLine,
  onReorderLines,
  onCreateStatus,
  onCreateItem,
  onCreateUom,
  onSave,
  onCancel = () => {},
  saving = false,
  canEdit = true,
  isDirty = false,
}: RequestMaterialFormProps) {
  const readOnly = !canEdit;
  const formatEmployeeName = (raw?: string) => {
    if (!raw) return '';
    // Remove leading code like "NV-00064 - " if present
    return raw.replace(/^[^\s-]+\s*-\s*/i, '').trim();
  };
  const selectedOrder = orderOptions.find((order) => order.id === formData.order_id) || null;
  const selectedStatus = statusOptions.find((status) => {
    const currentStatus = normalizeStatusValue(formData.status);
    return normalizeStatusValue(status.id) === currentStatus || normalizeStatusValue(status.code) === currentStatus;
  }) || null;
  const statusColor = selectedStatus?.color || '';

  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!formData.order_id) {
      setOrderDetail(null);
      return;
    }
    let cancelled = false;
    setOrderDetailLoading(true);
    getOrderById(formData.order_id)
      .then((response) => {
        if (!cancelled) setOrderDetail(response.data);
      })
      .catch(() => {
        if (!cancelled) setOrderDetail(null);
      })
      .finally(() => {
        if (!cancelled) setOrderDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [formData.order_id]);

  const cancelClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const scheduleClose = () => {
    closeTimerRef.current = setTimeout(() => setPreviewAnchor(null), 120);
  };

  const handlePreviewMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    cancelClose();
    const target = event.currentTarget;
    openTimerRef.current = setTimeout(() => setPreviewAnchor(target), 150);
  };

  const handlePreviewMouseLeave = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    scheduleClose();
  };

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={saving || readOnly || !isDirty}
      submitLabel='Lưu request'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Thông tin phiếu yêu cầu</Typography>

        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField
              label='Đơn hàng'
              labelWidth={100}
              fieldAlign='center'
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  py: 0.5,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AddButtonAutocomplete
                    options={orderOptions.map((order) => ({ ...order, name: `${order.order_no} - ${order.name}` }))}
                    value={selectedOrder ? { ...selectedOrder, name: `${selectedOrder.order_no} - ${selectedOrder.name}` } : null}
                    onChange={(_event, newValue) => {
                      const order = newValue as (RequestMaterialOrderOption & { name: string }) | null;
                      onChangeForm({
                        order_id: order?.id || '',
                        request_type: order ? 'order' : 'reserve',
                      });
                    }}
                    onInputChange={(_event, value, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        onChangeForm({ order_search: value });
                      }
                    }}
                    loading={orderLoading}
                    filterOptions={(options) => options}
                    placeholder='Chọn đơn hàng'
                    disabled={readOnly}
                    noOptionsText={formData.order_search?.trim() ? 'Không tìm thấy đơn hàng phù hợp' : 'Nhập mã hoặc tên đơn hàng để tìm'}
                    fullWidth
                  />
                </Box>

                <Tooltip title={selectedOrder ? '' : 'Chọn đơn hàng để xem nhanh'} placement='bottom-start'>
                  <span
                    onMouseEnter={selectedOrder ? handlePreviewMouseEnter : undefined}
                    onMouseLeave={selectedOrder ? handlePreviewMouseLeave : undefined}
                  >
                    <IconButton
                      aria-label='Preview đơn hàng'
                      disabled={!selectedOrder || readOnly}
                      sx={{
                        ml: 'auto',
                        p: 0.5,
                        minWidth: 'auto',
                        minHeight: 'auto',
                        color: selectedOrder ? '#64748b' : '#94a3b8',
                        backgroundColor: 'transparent',
                        transition: 'color 0.18s ease-in-out, background-color 0.18s ease-in-out',
                        '&:hover': {
                          color: '#2563eb',
                          backgroundColor: 'rgba(37, 99, 235, 0.08)',
                        },
                        '&:active': {
                          backgroundColor: 'rgba(37, 99, 235, 0.14)',
                        },
                      }}
                    >
                      <VisibilityOutlined fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>

                <Popover
                  open={Boolean(previewAnchor)}
                  anchorEl={previewAnchor}
                  onClose={() => setPreviewAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  disableRestoreFocus
                  disableScrollLock
                  onMouseEnter={cancelClose}
                  onMouseLeave={scheduleClose}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 340,
                        maxWidth: 460,
                        maxHeight: 420,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 8px 24px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06)',
                        overflow: 'hidden',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.65)',
                        mb: 0.25,
                      }}
                    >
                      Đơn hàng
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          color: '#fff',
                          fontFamily: '"Roboto Mono", monospace',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {selectedOrder?.order_no}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: 'rgba(255,255,255,0.75)',
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedOrder?.name}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
                    {orderDetailLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 3 }}>
                        <CircularProgress size={16} thickness={4} sx={{ color: '#2563eb' }} />
                        <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>Đang tải...</Typography>
                      </Box>
                    ) : orderDetail?.items?.length > 0 ? (
                      <>
                        {(orderDetail.items as any[]).map((item: any, index: number) => {
                          if (item.isGroup) {
                            return (
                              <Box
                                key={index}
                                sx={{
                                  px: 2,
                                  pt: index > 0 ? 1.5 : 0.75,
                                  pb: 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ width: 3, height: 12, borderRadius: '2px', backgroundColor: '#2563eb', flexShrink: 0 }} />
                                <Typography
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.07em',
                                    textTransform: 'uppercase',
                                    color: '#334155',
                                  }}
                                >
                                  {item.groupName || item.product_name}
                                </Typography>
                              </Box>
                            );
                          }
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 0.6,
                                gap: 1.5,
                                '&:hover': { backgroundColor: '#f8fafc' },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 4,
                                  height: 4,
                                  borderRadius: '50%',
                                  backgroundColor: '#cbd5e1',
                                  flexShrink: 0,
                                  ml: 0.5,
                                }}
                              />
                              <Typography
                                sx={{
                                  flex: 1,
                                  fontSize: '0.825rem',
                                  color: '#1e293b',
                                  lineHeight: 1.4,
                                }}
                              >
                                {item.product_name}
                              </Typography>
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  px: 1,
                                  py: 0.2,
                                  borderRadius: '4px',
                                  backgroundColor: '#f1f5f9',
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: '#475569',
                                    whiteSpace: 'nowrap',
                                    fontFamily: '"Roboto Mono", monospace',
                                  }}
                                >
                                  {item.quantity} <span style={{ fontWeight: 400, color: '#94a3b8' }}>{item.unit_name}</span>
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </>
                    ) : (
                      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.825rem', color: '#94a3b8' }}>Không có item nào.</Typography>
                      </Box>
                    )}
                  </Box>

                  {!orderDetailLoading && orderDetail?.items?.length > 0 && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderTop: '1px solid #f1f5f9',
                        backgroundColor: '#f8fafc',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        {(orderDetail.items as any[]).filter((i: any) => !i.isGroup).length} sản phẩm
                      </Typography>
                    </Box>
                  )}
                </Popover>
              </Box>
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Kho nhận' labelWidth={100}>
              <TextField
                select
                size='small'
                value={formData.warehouse_id}
                onChange={(event) => onChangeForm({ warehouse_id: event.target.value })}
                disabled={readOnly}
                fullWidth
              >
                {warehouseOptions.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Trạng thái' labelWidth={100}>
              <Autocomplete
                options={statusOptions}
                value={selectedStatus}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: 0, margin: 0 }}>
                    <Typography sx={{ color: option.color || 'inherit', px: 2, py: 0.5 }}>
                      {option.name}
                    </Typography>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder='Chọn trạng thái'
                    size='small'
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: statusColor || formData.status_color || 'inherit',
                        fontWeight: statusColor || formData.status_color ? 500 : 'inherit',
                      },
                    }}
                  />
                )}
                onChange={(_event, newValue) => {
                  const nextStatus = newValue as { id: string; name: string; color?: string } | null;
                  onChangeForm({
                    status: nextStatus?.id || '',
                    status_color: nextStatus?.color || '',
                  });
                }}
                noOptionsText='Không tìm thấy trạng thái'
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Ngày đề xuất' labelWidth={100}>
              <DatePicker
                value={formData.requested_at ? new Date(formData.requested_at) : null}
                onChange={(date) => onChangeForm({ requested_at: date ? (date as Date).toISOString().slice(0, 10) : null })}
                disabled={readOnly}
                format='dd/MM/yyyy'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'Chọn ngày đề xuất',
                    sx: sharedFormStyles.datePickerArrowAlign,
                  },
                }}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Ngày cần có' labelWidth={100}>
              <DatePicker
                value={formData.required_by ? new Date(formData.required_by) : null}
                onChange={(date) => onChangeForm({ required_by: date ? (date as Date).toISOString().slice(0, 10) : null })}
                disabled={readOnly}
                format='dd/MM/yyyy'
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    placeholder: 'Chọn ngày cần có vật tư',
                    sx: sharedFormStyles.datePickerArrowAlign,
                  },
                }}
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Người đề xuất' labelWidth={100}>
              <AddButtonAutocomplete
                options={employeeOptions}
                value={employeeOptions.find((o) => o.id === formData.requested_by_id) || (formData.requested_by_id ? { id: formData.requested_by_id, name: '' } : null)}
                onChange={(_e, newVal) => {
                  const v = newVal as any;
                  onChangeForm({ requested_by_id: v ? v.id : null });
                }}
                placeholder='Chọn hoặc tìm nhân viên'
                noOptionsText='Không tìm thấy nhân viên'
                getOptionLabel={(opt: any) => formatEmployeeName(opt?.name)}
                disabled={readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Danh sách vật tư yêu cầu</Typography>
        <RequestMaterialItemsTable
          lines={formData.lines}
          itemOptions={itemOptions}
          uomOptions={uomOptions}
          companyId={formData.company_id}
          warehouseId={formData.warehouse_id}
          companyName={companyName}
          warehouseName={warehouseOptions.find((w) => w.id === formData.warehouse_id)?.name}
          requiredBy={formData.required_by}
          orderId={formData.order_id || undefined}
          orderNo={orderOptions.find((o) => o.id === formData.order_id)?.order_no}
          orderName={orderOptions.find((o) => o.id === formData.order_id)?.name}
          materialRequestId={materialRequestId}
          requestedById={formData.requested_by_id || undefined}
          onChangeLine={onChangeLine}
          onAddItem={onAddItem}
          onRemoveLine={onRemoveLine}
          onReorderLines={onReorderLines}
          onCreateItem={onCreateItem}
          onCreateUom={onCreateUom}
          canEdit={canEdit}
        />
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Ghi chú</Typography>
        <TextareaAutosize
          minRows={4}
          value={formData.note || ''}
          onChange={(event) => onChangeForm({ note: event.target.value })}
          disabled={readOnly}
          placeholder='Nhập ghi chú chung cho toàn phiếu request...'
          style={sharedFormStyles.additionalTextarea(canEdit)}
        />
      </Paper>
    </AppForm>
  );
}
